import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { supabaseAdmin } from '../config/supabase';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../components/ui/use-toast';
import { compressImage, AVATAR_PRESET, validateImageFile, formatFileSize } from '../utils/imageCompression';
import { validateWebsite } from '../utils/inputValidation';
import slugify from '../utils/slugify';

// Business type configuration - maps type to database table names
const BUSINESS_TYPE_CONFIG = {
  trader: {
    label: 'Trades & Services',
    table: 'professions',        // Table containing professions for this type
    targetTable: 'traders',      // Table where business data is stored
  },
  craftie: {
    label: 'Local Makers',
    table: 'craft_professions',
    targetTable: 'crafties',
  },
  hospitality: {
    label: 'Hospitality',
    table: 'hospitality_categories',
    targetTable: 'hospitality',
  },
  professional: {
    label: 'Professionals',
    table: 'professionals_categories',
    targetTable: 'professionals',
  },
} as const;

type BusinessType = keyof typeof BUSINESS_TYPE_CONFIG;

interface CraftsmanFormData {
  store_name: string;
  owner_name: string;
  phone_number: string;
  location: string;
  description: string;
  business_type: BusinessType;
  category: string;
  profession_id: number;
  avatar_url?: string;
  website?: string;
}

interface CraftsmanRegistrationProps {
  initialData?: CraftsmanFormData;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
  isEditing?: boolean;
}

const CraftsmanRegistration = ({ initialData, onSuccess, onCancel, isEditing = false }: CraftsmanRegistrationProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // State for cascading dropdowns
  const [categories, setCategories] = useState<string[]>([]);
  const [professions, setProfessions] = useState<{ id: number; name: string; category?: string }[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProfessions, setLoadingProfessions] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(initialData?.avatar_url || null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<CraftsmanFormData>({
    defaultValues: initialData || {
      store_name: '',
      owner_name: '',
      phone_number: '',
      location: '',
      description: '',
      business_type: undefined as unknown as BusinessType,
      category: '',
      profession_id: undefined as unknown as number,
    }
  });

  // Watch business_type and category for dynamic profession loading
  const selectedBusinessType = watch('business_type');
  const selectedCategory = watch('category');

  // Fetch categories when business type changes
  useEffect(() => {
    const fetchCategories = async () => {
      // Reset category and profession when type changes
      setValue('category', '');
      setValue('profession_id', undefined as unknown as number);
      setCategories([]);
      setProfessions([]);
      
      if (!selectedBusinessType) {
        return;
      }

      const config = BUSINESS_TYPE_CONFIG[selectedBusinessType];
      if (!config) {
        return;
      }

      try {
        setLoadingCategories(true);
        
        // Fetch distinct categories from the profession table
        const { data, error } = await supabaseAdmin
          .from(config.table)
          .select('category')
          .order('category');

        if (error) throw error;
        
        // Extract unique categories
        const uniqueCategories = [...new Set(data?.map(item => item.category).filter(Boolean))] as string[];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
        toast({
          title: 'Error',
          description: 'Failed to load categories',
          variant: 'destructive'
        });
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [selectedBusinessType, setValue]);

  // Fetch professions when category changes
  useEffect(() => {
    const fetchProfessions = async () => {
      // Reset profession when category changes
      setValue('profession_id', undefined as unknown as number);
      setProfessions([]);
      
      if (!selectedBusinessType || !selectedCategory) {
        return;
      }

      const config = BUSINESS_TYPE_CONFIG[selectedBusinessType];
      if (!config) {
        return;
      }

      try {
        setLoadingProfessions(true);
        
        const { data, error } = await supabaseAdmin
          .from(config.table)
          .select('id, name, category')
          .eq('category', selectedCategory)
          .order('name');

        if (error) throw error;
        setProfessions(data || []);
      } catch (err) {
        console.error('Error fetching professions:', err);
        toast({
          title: 'Error',
          description: 'Failed to load professions',
          variant: 'destructive'
        });
        setProfessions([]);
      } finally {
        setLoadingProfessions(false);
      }
    };

    fetchProfessions();
  }, [selectedBusinessType, selectedCategory, setValue]);

  // Handle avatar file selection with automatic compression (same as TraderRegistration)
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate the image file first
    const validation = validateImageFile(file, 20); // Allow up to 20MB before compression
    if (!validation.isValid) {
      toast({
        title: 'Invalid Image',
        description: validation.error || 'Please select a valid image file',
        variant: 'destructive'
      });
      return;
    }

    // Show preview immediately from original file
    setAvatarPreview(URL.createObjectURL(file));

    // Compress the image in the background
    const originalSize = file.size;
    setIsCompressing(true);
    setCompressionInfo(`Compressing image (${formatFileSize(originalSize)})...`);

    try {
      // Use AVATAR_PRESET for profile images (max 500KB, 800px)
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      
      // Calculate reduction percentage
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(0);
      
      setAvatarFile(compressedFile);
      setCompressionInfo(
        `Image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
      );
    } catch (err) {
      console.error('Compression failed, using original:', err);
      setAvatarFile(file); // Fall back to original file
      setCompressionInfo('Using original image');
    } finally {
      setIsCompressing(false);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;

    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `craftsmen/${fileName}`;

      const { error: uploadError } = await supabaseAdmin.storage
        .from('avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('avatars')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return null;
    }
  };

  // Generate a unique slug for the business, checking for duplicates in the target table
  const generateUniqueSlug = async (storeName: string, tableName: string): Promise<string> => {
    const base = slugify(storeName) || 'business';
    let candidate = base;
    let suffix = 2;

    while (true) {
      const { data, error } = await supabaseAdmin
        .from(tableName)
        .select('id')
        .eq('slug', candidate)
        .limit(1);

      if (error) {
        console.error(`Error checking slug uniqueness in ${tableName}:`, error);
        // If error, add timestamp to make it unique
        return `${base}-${Date.now()}`;
      }

      if (!data || data.length === 0) {
        return candidate;
      }

      candidate = `${base}-${suffix}`;
      suffix++;
    }
  };

  const onSubmit = async (formData: CraftsmanFormData) => {
    // Ensure user is authenticated before proceeding
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to register your business.',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      console.log('Form data:', formData);

      const avatarUrl = await uploadAvatar();
      if (avatarFile && !avatarUrl) {
        throw new Error('Failed to upload avatar');
      }

      // Get the target table based on business type
      const config = BUSINESS_TYPE_CONFIG[formData.business_type];
      if (!config) {
        throw new Error('Invalid business type selected');
      }

      // Generate unique slug for the business
      const slug = await generateUniqueSlug(formData.store_name, config.targetTable);

      // Resolve the correct business_type_id at submit time for tables that support it.
      // This avoids race conditions and does not rely on component state.
      let businessTypeId: string | null = null;
      if (config.targetTable === 'traders' || config.targetTable === 'hospitality' || config.targetTable === 'professionals') {
        const { data: btData, error: btError } = await supabaseAdmin
          .from('business_types')
          .select('id, name');

        if (btError) {
          console.error('Error fetching business_types in onSubmit:', btError);
        } else if (btData && btData.length > 0) {
          const match = btData.find((bt: any) => {
            const name = (bt.name || '').toLowerCase();
            if (formData.business_type === 'trader') return name === 'tradesman';
            if (formData.business_type === 'craftie') return name === 'craftsman';
            if (formData.business_type === 'hospitality') return name === 'hospitality';
            if (formData.business_type === 'professional') return name.includes('professional');
            return false;
          });
          businessTypeId = match?.id ?? null;
        }

        console.log('Resolved business_type_id for', formData.business_type, '→', businessTypeId);
      }

      // For OAuth users (e.g. Gmail), Supabase sets email_confirmed_at immediately.
      // In that case we can safely mark the business as verified on creation.
      const isEmailConfirmed = !!(user as any).email_confirmed_at;
      console.log('User email_confirmed_at:', (user as any).email_confirmed_at, '→ isEmailConfirmed =', isEmailConfirmed);

      // Build business data using the authenticated user's info
      const businessData: any = {
        store_name: formData.store_name,
        owner_name: formData.owner_name,
        email: user.email,
        phone_number: formData.phone_number,
        location: formData.location,
        description: formData.description,
        profession_id: formData.profession_id,
        website: formData.website || null,
        avatar_url: avatarUrl || null,
        verified: isEmailConfirmed,
        user_id: user.id,
        slug: slug
      };

      // Only attach business_type_id for tables that actually have this column.
      if (businessTypeId && (config.targetTable === 'traders' || config.targetTable === 'hospitality' || config.targetTable === 'professionals')) {
        businessData.business_type_id = businessTypeId;
      }

      console.log(`Business data to insert into ${config.targetTable}:`, businessData);

      if (isEditing) {
        if (onSuccess) {
          onSuccess(businessData);
        }
      } else {
        const { data, error: insertError } = await supabaseAdmin
          .from(config.targetTable)
          .insert([businessData])
          .select();

        if (insertError) {
          console.error('Insert error:', insertError);
          throw new Error(insertError.message || 'Failed to register business');
        }

        if (!data) {
          throw new Error('No data returned after insert');
        }

        toast({
          title: 'Success',
          description: 'Business registered successfully!',
        });

        // Navigate to dashboard after successful registration
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit registration',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage: `url('/craftsman-hero.png')`,
        backgroundAttachment: 'fixed'
      }}>
      <div className="relative z-10 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white/25 backdrop-blur-md rounded-2xl shadow-lg border border-white/30 p-6 sm:p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEditing ? 'Edit Business Profile' : 'Register Your Business'}</h1>
            <p className="text-gray-700">Join our marketplace and showcase your services</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Avatar Upload Section */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative w-32 h-32 mb-4">
                <div className={`w-full h-full rounded-full overflow-hidden bg-white/40 backdrop-blur-sm border-4 border-white/50 shadow-lg ${!avatarPreview ? 'flex items-center justify-center' : ''}`}>
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl text-gray-600">
                      {watch('owner_name')?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <label
                  htmlFor="avatar-upload"
                  className={`absolute bottom-0 right-0 bg-white/70 backdrop-blur-sm rounded-full p-2 shadow-lg cursor-pointer hover:bg-white/90 transition-colors border border-white/50 ${isCompressing ? 'opacity-50 cursor-wait' : ''}`}
                >
                  {isCompressing ? (
                    <svg className="h-6 w-6 text-gray-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={isCompressing}
                />
              </div>
              <p className="text-sm text-gray-700">Upload your profile picture</p>
              {/* Show compression status/result */}
              {compressionInfo && (
                <p className={`text-xs mt-1 ${isCompressing ? 'text-gray-500' : 'text-green-600'}`}>
                  {!isCompressing && '✓ '}{compressionInfo}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="store_name" className="block text-sm font-medium text-gray-700">Business Name</Label>
                  <Input
                    id="store_name"
                    {...register('store_name', { required: 'Business name is required' })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                  />
                  {errors.store_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.store_name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="owner_name" className="block text-sm font-medium text-gray-700">Owner Name</Label>
                  <Input
                    id="owner_name"
                    {...register('owner_name', { required: 'Owner name is required' })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                  />
                  {errors.owner_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.owner_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">Phone Number</Label>
                  <Input
                    id="phone_number"
                    type="tel"
                    inputMode="numeric"
                    placeholder="087 123 4567 or (087) 123-4567"
                    {...register('phone_number', {
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[0-9+\-\s()]*$/,
                        message: 'Please enter a valid phone number (e.g., 087 123 4567 or (049) 952 6936)'
                      }
                    })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                  />
                  {errors.phone_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone_number.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    id="website"
                    type="text"
                    placeholder="example.com or www.example.com"
                    {...register('website', {
                      validate: validateWebsite
                    })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</Label>
                  <Input
                    id="location"
                    {...register('location', { required: 'Location is required' })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                  />
                  {errors.location && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
                  )}
                </div>

                {/* Business Type Select */}
                <div>
                  <Label htmlFor="business_type" className="block text-sm font-medium text-gray-700">Business Type</Label>
                  <Select
                    value={selectedBusinessType}
                    onValueChange={(value) => setValue('business_type', value as BusinessType)}
                  >
                    <SelectTrigger className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500">
                      <SelectValue placeholder="Select business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(BUSINESS_TYPE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.business_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.business_type.message}</p>
                  )}
                </div>

                {/* Category Select - only show when business type is selected */}
                {selectedBusinessType && (
                  <div>
                    <Label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</Label>
                    <Select
                      value={selectedCategory}
                      onValueChange={(value) => setValue('category', value)}
                      disabled={loadingCategories}
                    >
                      <SelectTrigger className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500">
                        <SelectValue placeholder={loadingCategories ? "Loading..." : "Select category"} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                    )}
                  </div>
                )}

                {/* Profession Select - only show when category is selected */}
                {selectedCategory && (
                  <div>
                    <Label htmlFor="profession" className="block text-sm font-medium text-gray-700">Profession</Label>
                    <Select
                      value={watch('profession_id')?.toString()}
                      onValueChange={(value) => setValue('profession_id', parseInt(value))}
                      disabled={loadingProfessions}
                    >
                      <SelectTrigger className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500">
                        <SelectValue placeholder={loadingProfessions ? "Loading..." : "Select profession"} />
                      </SelectTrigger>
                      <SelectContent>
                        {professions.map((profession) => (
                          <SelectItem key={profession.id} value={profession.id.toString()}>
                            {profession.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.profession_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.profession_id.message}</p>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</Label>
                  <Textarea
                    id="description"
                    {...register('description', { required: 'Description is required' })}
                    className="mt-1 bg-white/50 backdrop-blur-sm border-white/30 focus:border-white/50 focus:ring-blue-500"
                    rows={4}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4 pt-6">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="w-full sm:w-auto px-8 bg-white/50 backdrop-blur-sm hover:bg-white/70"
                  disabled={loading}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className="w-full sm:w-auto px-8 bg-gradient-to-r from-blue-600/90 to-blue-700/90 hover:from-blue-700/90 hover:to-blue-800/90 backdrop-blur-sm"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  isEditing ? 'Save Changes' : 'Register Business'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CraftsmanRegistration;
